import { HttpClient } from "@cloudpayments/api-client";
import { DomainConfiguration } from "@src/runtime-configuration/DomainConfiguration";

export class FeatureApiService {
  private client: HttpClient = new HttpClient(
    `${window.location.origin}/feature-api`
  );
  private readonly timeout: number = 15;
  private readonly toggleMap: Map<string, boolean> = new Map<string, boolean>();
  private config: DomainConfiguration | undefined;

  public async loadToggleData(path: string, filter?: string) {
    const url = FeatureApiService.getUrl(path, filter);
    const result = await this.isToggleEnabled(url);

    this.toggleMap.set(url.search, result);
  }

  public getToggleData(path: string, filter?: string) {
    const url = FeatureApiService.getUrl(path, filter);

    return this.toggleMap.get(url.search);
  }

  private async isToggleEnabled(url: URL) {
    const result = await this.getWithTimeout<IFeatureApiToggle[]>(
      "toggles",
      url
    );

    if (typeof result != "boolean") {
      return result.data[0]?.value ?? false;
    }

    return result;
  }

  private async getWithTimeout<T>(patch: string, url: URL) {
    try {
      const result = await Promise.race<IFeatureApiResponse<T>>([
        new Promise((res, rej) =>
          setTimeout(() => rej(false), this.timeout * 1000)
        ),
        this.client.get<IFeatureApiResponse<T>>(
          `${window.location.origin}/feature-api/${patch}/${url.search}`
        ),
      ]);

      return result;
    } catch (e) {
      return false;
    }
  }

  public async loadConfiguration(): Promise<DomainConfiguration | undefined> {
    if (!this.config) {
      const url = FeatureApiService.getUrl();

      return this.getWithTimeout<DomainConfiguration>("config", url).then(
        (result) => {
          if (typeof result !== "boolean") {
            this.config = result.data;
          }

          return this.config;
        }
      );
    } else {
      return Promise.resolve(this.config);
    }
  }

  private static getUrl(path?: string, filter?: string) {
    const url = new URL(`${window.location.origin}`, window.location.origin);
    if (path != null) {
      url.searchParams.append("path", path);
    }
    if (filter != null) {
      url.searchParams.append("filter", filter);
    }

    return url;
  }
}

export interface IFeatureApiToggle {
  name: string;
  path: string;
  value: boolean;
}

export interface IFeatureApiResponse<T> {
  statusCode: number;
  message: string;
  data: T;
}
